import { DERIVED_COL_LABELS, PERIOD_AFTER, PERIOD_BEFORE, titleCase } from "./columns";
import type {
  CarriedManualRules, CarriedMatchPair, FrameRecords, PriorOthers, SideDisplayConfig,
  UnreconciledBlock,
} from "../types/api";

// Prior-period items in the Manual Match books (finance 2026-10-08, AL BUHAIRA demo): each
// book's grid holds this period's unmatched rows AND that book's still-open carried items,
// and one basket decides what a selection becomes. Pure functions, ported to
// tests/prior_period_match_port.py: keep the two in lockstep.

export type Book = "insurer" | "cri";

// The carried frame's own signature column (backend/carried_keys.py::carried_signatures).
export const CARRIED_SIG = "unrec_sig";
// Every carried signature starts with this; the engine frames' start "CRI|" / "INS|", so a
// ticked row's origin is read off its signature with no lookup.
export const PRIOR_SIG_PREFIX = "UNREC|";
export const isPriorSig = (sig: string): boolean => sig.startsWith(PRIOR_SIG_PREFIX);

// Amount columns for a CURRENT-PERIOD row, per side. Mirrors _apply_carried_manual's
// amt_cols exactly: the two books name the concept differently.
export const CRI_AMOUNT = "cri_amount";
export const INS_AMOUNTS = ["balance_amount", "signed_amount"];

// Amount columns for a CARRIED row. MUST mirror backend _AMOUNT_COLS in order,
// cri_amount included: _key_frame takes the FIRST present, and a carried file that
// carries only cri_amount would otherwise read as 0.00 here and make the preview claim
// a settlement the engine will refuse.
// "amount" is the RAW column parse_cri_os emits, and a CRI-side carried list uploaded on
// its own (a first reconciliation) has NO other.
export const CARRIED_AMOUNTS = ["balance_amount", "signed_amount", "cri_amount", "amount"];

// A carried item's policy, for "Find this policy" (the carried frame has no `_pol`).
export const CARRIED_POLICY = ["pol_endt_clm_no", "policy_no"];

// The ledger's derived Movement (Debit - Credit) column: the figure finance reads.
export const MOVEMENT = "movement";

// The engine's track for a carried item (backend carried_ladder._carried_item_tracks), and
// the chip an item nothing decides sits under, so a track chip never hides one.
export const CARRIED_TRACK = "unrec_track";
export const UNKNOWN_TRACK = "unknown";

// Which period a grid row comes from. Shown only when the book holds prior-period items.
export const SOURCE_COL = "mm_source";
export const SOURCE_LABEL = "Source";
export const THIS_PERIOD = "This period";
export const PRIOR_PERIOD = "Prior period";
export const PRIOR_OTHERS_SOURCE = "Prior period · Others";
// Marks a row of the previous output's Others tab (api/serialize _prior_others_to_json).
export const PRIOR_OTHERS_COL = "unrec_prior_others";

const EMPTY: FrameRecords = { columns: [], rows: [], indices: [], total_rows: 0 };

// The first of `names` present in the frame, or -1.
export function colIndex(frame: FrameRecords, names: string[]): number {
  for (const n of names) {
    const i = frame.columns.indexOf(n);
    if (i >= 0) return i;
  }
  return -1;
}

// The amount of the row carrying `sig`, or null: an unreadable leg is not a zero.
export function amountFor(frame: FrameRecords, sigCol: string, sig: string,
                          amountNames: string[]): number | null {
  const si = frame.columns.indexOf(sigCol);
  const ai = colIndex(frame, amountNames);
  if (si < 0 || ai < 0) return null;
  const row = frame.rows.find((r) => String(r[si] ?? "") === sig);
  const v = row?.[ai];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// One book's carried list. cri_rows is null on an insurer-only upload.
export function carriedRows(block: UnreconciledBlock | null | undefined, book: Book): FrameRecords {
  if (!block) return EMPTY;
  return (book === "cri" ? block.cri_rows : block.rows) ?? EMPTY;
}

export function carriedSourceColumns(block: UnreconciledBlock | null | undefined,
                                     book: Book): string[] | undefined {
  return book === "cri" ? block?.cri_source_columns : block?.source_columns;
}

// A carried item's reference. MUST mirror backend carried_keys._REF_COLS in order.
export const CARRIED_REFS = ["doc_reference", "insurer_inv_no", "reference_no"];

// The prior-period item a ledger row settled, read off the row's match_group_id
// "PP:<list>:<item index>" (backend carried_link._CARRIED_GID). `undo` is how the search
// panel undoes the pair where it was found: an automatic clear is REJECTED (the item held
// open), a hand pair FORGOTTEN through `pair`, the stored entry the engine applied. A hand
// pair whose entry the result does not name gets no undo (rejecting it would do nothing:
// a hand pair outranks a rejection). Null for any row the pass did not stamp.
export interface PriorSettlement {
  book: Book;
  sig: string;
  ref: string;
  amount: number | null;
  undo: "reject" | "forget" | null;
  pair: CarriedMatchPair | null;
}

export function priorSettlementOf(block: UnreconciledBlock | null | undefined,
                                  gid: string): PriorSettlement | null {
  const m = /^PP:(insurer|cri):(.+)$/.exec(gid);
  if (!m) return null;
  const book = m[1] as Book;
  const items = carriedRows(block, book);
  const si = items.columns.indexOf(CARRIED_SIG);
  const pos = items.indices.findIndex((i) => String(i ?? "") === m[2]);
  if (si < 0 || pos < 0) return null;
  const row = items.rows[pos];
  const sig = String(row[si] ?? "");
  if (!sig) return null;
  const at = (name: string) => items.columns.indexOf(name);
  const ri = colIndex(items, CARRIED_REFS);
  const ai = colIndex(items, CARRIED_AMOUNTS);
  const amt = ai >= 0 ? row[ai] : null;
  const outcomes = book === "cri"
    ? [...(block?.cri_manual_applied ?? []), ...(block?.cri_manual_untied ?? [])]
    : [...(block?.manual_applied ?? []), ...(block?.manual_untied ?? [])];
  const pair = outcomes.find((o) => o.carried_sig === sig && o.pair)?.pair ?? null;
  const manual = pair !== null || row[at("unrec_cleared_by_tier")] === "Manual"
    || Boolean(row[at("unrec_manual_held")]);
  return {
    book, sig,
    ref: ri >= 0 ? String(row[ri] ?? "").trim() : "",
    amount: typeof amt === "number" && Number.isFinite(amt) ? amt : null,
    undo: !manual ? "reject" : pair ? "forget" : null,
    pair,
  };
}

// The carried rows that are STILL OPEN: the ones there is anything to match. Excluded
// (heading/total) rows are never items, and settled ones are already discharged.
export function openCarriedSlice(frame: FrameRecords): FrameRecords {
  const mi = frame.columns.indexOf("unrec_matched");
  const xi = frame.columns.indexOf("unrec_excluded");
  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];
  frame.rows.forEach((r, ri) => {
    if (xi >= 0 && r[xi]) return;
    if (mi >= 0 && r[mi]) return;
    rows.push(r);
    indices.push(frame.indices[ri] ?? ri);
  });
  return { columns: frame.columns, rows, indices, total_rows: rows.length };
}

// Finance's OWN columns as a CURATED list: allColumns' extras rule would hide engine
// names ("role", "signed_amount") that here are headers finance typed.
export function carriedConfig(frame: FrameRecords, src?: string[]): SideDisplayConfig {
  const cols = src?.length ? src : frame.columns.filter((c) => !c.startsWith("unrec_"));
  return { columns: cols.filter((c) => c !== CARRIED_SIG), renames: {} };
}

// Which ledger column each carried file column is: the same name, else the same ON-SCREEN
// name (the label the grid renders, from `config` or the extras rule). Finance's own list
// is read by the same parser as the ledger, so its names already agree; last month's
// OUTPUT is this app's export, headed with those labels ("Doc Date", "Amount (AED)"), and
// matched by name alone every one of its fields became a second, same-titled column
// (AL BUHAIRA Aug on Jun's output: 74 CRI / 72 insurer columns, every one aligns by label).
// A column with no partner, or whose partner is already taken, keeps its own.
export function alignCarriedColumns(ledgerCols: string[], fileCols: string[],
                                    config?: SideDisplayConfig): Map<string, string> {
  const byLabel = new Map<string, string>();
  for (const k of ledgerCols) {
    if (k.startsWith("_")) continue;
    for (const lab of [config?.renames[k], DERIVED_COL_LABELS[k], titleCase(k)]) {
      const key = (lab ?? "").trim().toLowerCase();
      if (key && !byLabel.has(key)) byLabel.set(key, k);
    }
  }
  const out = new Map<string, string>();
  const taken = new Set<string>();
  for (const c of fileCols) {
    const to = ledgerCols.includes(c) ? c : byLabel.get(c.trim().toLowerCase());
    const target = to && !taken.has(to) ? to : c;
    taken.add(target);
    out.set(c, target);
  }
  return out;
}

// The book's grid: this period's unmatched rows, then the carried list's still-open items
// under the ledger's own columns (alignCarriedColumns). A prior row's `amountCol` holds the
// figure the engine settles the item on (the first CARRIED_AMOUNTS column present), its
// `_sig` its carried signature, its `_pol` its policy and its `track` the engine's track
// (else UNKNOWN_TRACK), so the finds, the track chips and the tray read it like any other
// row. Returns `ledger` itself when the book has no open item, so no other run pays anything.
export function withPriorRows(ledger: FrameRecords, carried: FrameRecords,
                              sourceCols: string[] | undefined, amountCol: string,
                              config?: SideDisplayConfig): FrameRecords {
  const open = openCarriedSlice(carried);
  const si = open.columns.indexOf(CARRIED_SIG);
  if (open.total_rows === 0 || si < 0) return ledger;
  const fileCols = carriedConfig(open, sourceCols).columns;
  const target = alignCarriedColumns(ledger.columns, fileCols, config);
  const columns = [...ledger.columns];
  for (const c of [...fileCols.map((f) => target.get(f)!), amountCol, "_sig", "_pol", "track"]) {
    if (!columns.includes(c)) columns.push(c);
  }
  columns.push(SOURCE_COL);
  const n = columns.length;
  const at = new Map(columns.map((c, i) => [c, i]));
  const copy = fileCols
    .map((c) => [open.columns.indexOf(c), at.get(target.get(c)!)!] as const)
    .filter(([from]) => from >= 0);
  const ai = colIndex(open, CARRIED_AMOUNTS);
  const pi = colIndex(open, CARRIED_POLICY);
  const ti = open.columns.indexOf(CARRIED_TRACK);
  const oi = open.columns.indexOf(PRIOR_OTHERS_COL);
  const toAmt = at.get(amountCol)!;
  const toSig = at.get("_sig")!;
  const toPol = at.get("_pol")!;
  const toTrack = at.get("track")!;
  const toMv = at.get(MOVEMENT);

  // One shared tail per this-period row: concat copies it, so sharing is safe.
  const tail = [...new Array(n - ledger.columns.length - 1).fill(null), THIS_PERIOD];
  const rows: FrameRecords["rows"] = ledger.rows.map((r) => r.concat(tail));
  const indices: FrameRecords["indices"] = [...ledger.indices];
  open.rows.forEach((r, p) => {
    const out: FrameRecords["rows"][number] = new Array(n).fill(null);
    for (const [from, to] of copy) out[to] = r[from];
    const amt = ai >= 0 ? r[ai] : null;
    out[toAmt] = typeof amt === "number" && Number.isFinite(amt) ? amt : null;
    // Last month's output leaves Movement blank on its carried rows (finance read "—"):
    // such a row shows the figure it settles on, as the export's carried rows do.
    if (toMv !== undefined && String(out[toMv] ?? "").trim() === "") out[toMv] = out[toAmt];
    out[toSig] = String(r[si] ?? "");
    const pol = pi >= 0 ? String(r[pi] ?? "").trim() : "";
    out[toPol] = pol || null;
    out[toTrack] = (ti >= 0 ? String(r[ti] ?? "") : "") || UNKNOWN_TRACK;
    out[n - 1] = oi >= 0 && r[oi] ? PRIOR_OTHERS_SOURCE : PRIOR_PERIOD;
    rows.push(out);
    // Strings, so a carried row's id can never collide with a ledger row's.
    indices.push(`prior:${open.indices[p] ?? p}`);
  });
  return { columns, rows, indices, total_rows: rows.length };
}

// The block with the previous output's Others rows appended to each book's list (finance
// 2026-10-14): they are matched by hand like any prior-period item, so the grids, the
// basket, the amounts and the labels all read them from here. The engine moves one onto
// its list when its pair settles or holds it on Group 3 (backend
// carried_link.fold_paired_prior_others).
// `block` itself when there are none, so no other run pays anything.
export function withPriorOthers(block: UnreconciledBlock | null | undefined,
                                others: PriorOthers | null | undefined): UnreconciledBlock | null {
  const add = others?.match;
  if (!add || (!add.cri?.rows.length && !add.insurer?.rows.length)) return block ?? null;
  const merge = (held: FrameRecords | null | undefined, extra?: FrameRecords): FrameRecords | null => {
    if (!extra?.rows.length) return held ?? null;
    const base = held ?? EMPTY;
    const columns = [...base.columns, ...extra.columns.filter((c) => !base.columns.includes(c))];
    const grow = new Array(columns.length - base.columns.length).fill(null);
    const at = columns.map((c) => extra.columns.indexOf(c));
    return {
      columns,
      rows: [...base.rows.map((r) => r.concat(grow)),
             ...extra.rows.map((r) => at.map((i) => (i >= 0 ? r[i] : null)))],
      // Strings, so an Others row's id can never collide with a list item's label.
      indices: [...base.indices, ...extra.indices.map((ix, p) => `others:${ix ?? p}`)],
      total_rows: base.rows.length + extra.rows.length,
    };
  };
  return {
    ...(block ?? {}),
    rows: merge(block?.rows, add.insurer) ?? EMPTY,
    cri_rows: merge(block?.cri_rows, add.cri),
  } as UnreconciledBlock;
}

// Carried column naming the ledger row that re-prints the item before the window
// (backend carried_link.LEDGER_COPY_COL); blank when it has none.
export const LEDGER_COPY = "unrec_ledger_copy";

// The ledger rows, by frame index, that are the same document as a prior-period item
// (finance 2026-10-10: one transaction offered twice, and still pickable after the item
// settled). Settled items count too, so a settled item's re-print cannot be used again.
export function ledgerCopies(carried: FrameRecords): Set<string> {
  const ci = carried.columns.indexOf(LEDGER_COPY);
  const out = new Set<string>();
  if (ci < 0) return out;
  for (const r of carried.rows) {
    const v = String(r[ci] ?? "").trim();
    if (v) out.add(v);
  }
  return out;
}

// `frame` without the rows whose index is in `hide`; the frame itself when none is.
export function withoutRows(frame: FrameRecords, hide: Set<string>): FrameRecords {
  if (!hide.size) return frame;
  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];
  frame.rows.forEach((r, i) => {
    if (hide.has(String(frame.indices[i]))) return;
    rows.push(r);
    indices.push(frame.indices[i]);
  });
  return rows.length === frame.rows.length ? frame
    : { columns: frame.columns, rows, indices, total_rows: rows.length };
}

// The grid's display config with the Source column leading the curated ones.
export function withSourceColumn(config: SideDisplayConfig | undefined): SideDisplayConfig {
  return {
    columns: [SOURCE_COL, ...(config?.columns ?? [])],
    renames: { ...(config?.renames ?? {}), [SOURCE_COL]: SOURCE_LABEL },
  };
}

// The distinct tracks of the rows carrying `sigs`, in first-seen order; the strictest of
// their tolerances judges the pair.
export function tracksOf(frame: FrameRecords, sigCol: string, sigs: string[]): string[] {
  const si = frame.columns.indexOf(sigCol);
  const ti = frame.columns.indexOf("track");
  if (si < 0 || ti < 0) return [];
  const want = new Set(sigs);
  const out: string[] = [];
  for (const r of frame.rows) {
    if (!want.has(String(r[si] ?? ""))) continue;
    const t = String(r[ti] ?? "");
    if (!out.includes(t)) out.push(t);
  }
  return out;
}

// The recon_category of each ticked row the engine will NOT re-stamp Matched beside an
// item (backend/carried_link.py::_hand_settleable_row: open, inside the window, and in a
// bucket a hand match settles). Without the bucket test a pair against pre-period history
// saved and then silently never applied (AL-ITTIHAD Aug: 1,709 of 1,725 insurer rows).
// The window is tested on its own because every out-of-window row is filed under "Other",
// which a hand match may otherwise settle. A result built before manual_rules existed is
// not filtered, as before.
export const OUT_OF_WINDOW_LABEL = "dated outside the period";
export function ineligibleCategories(frame: FrameRecords, sigs: string[],
                                     allowed: string[] | undefined): string[] {
  if (!allowed) return [];
  const si = frame.columns.indexOf("_sig");
  const ci = frame.columns.indexOf("recon_category");
  const wi = frame.columns.indexOf("in_period_window");
  if (si < 0 || ci < 0) return [];
  const ok = new Set(allowed);
  const want = new Set(sigs);
  const out: string[] = [];
  for (const r of frame.rows) {
    if (!want.has(String(r[si] ?? ""))) continue;
    const outside = wi >= 0 && (r[wi] === false || r[wi] === 0);
    const c = outside ? OUT_OF_WINDOW_LABEL : String(r[ci] ?? "");
    if ((outside || !ok.has(c)) && !out.includes(c)) out.push(c);
  }
  return out;
}

// How many ticked rows are dated after the period (summary.PERIOD_AFTER). With every row
// so dated a tie clears the item there: it stays open, on Cleared in Subsequent Month
// (backend carried_link._hand_clears_after_row).
export function afterPeriodCount(frame: FrameRecords, sigs: string[]): number {
  const si = frame.columns.indexOf("_sig");
  const pi = frame.columns.indexOf("period_position");
  if (si < 0 || pi < 0) return 0;
  const want = new Set(sigs);
  return frame.rows.filter((r) => want.has(String(r[si] ?? "")) && r[pi] === PERIOD_AFTER)
    .length;
}

// How many ticked rows are dated before the period. With every row so dated a tie settles
// the item and lists them beside it on Matched (backend carried_link._hand_settles_before_row).
export function beforePeriodCount(frame: FrameRecords, sigs: string[]): number {
  const si = frame.columns.indexOf("_sig");
  const pi = frame.columns.indexOf("period_position");
  if (si < 0 || pi < 0) return 0;
  const want = new Set(sigs);
  return frame.rows.filter((r) => want.has(String(r[si] ?? "")) && r[pi] === PERIOD_BEFORE)
    .length;
}

// The frame indices of the ticked rows, as the result's per-book row lists name them.
function labelsOf(frame: FrameRecords, sigs: string[]): string[] {
  const si = frame.columns.indexOf("_sig");
  if (si < 0) return [];
  const want = new Set(sigs);
  const out: string[] = [];
  frame.rows.forEach((r, i) => {
    if (want.has(String(r[si] ?? ""))) out.push(String(frame.indices[i]));
  });
  return out;
}

type RowsByBook = { cri?: (number | string)[]; insurer?: (number | string)[] };

export type PriorKind = "cross" | "xlist" | "offset" | "basket";

export interface PriorVerdict {
  kind: PriorKind | null;
  // Why this basket cannot be saved; "" when it can (or is merely incomplete).
  refusal: string;
  // The carried list the item is on, and (cross) the ledger its settling rows sit on.
  book: Book;
  side: Book;
  pair: CarriedMatchPair | null;
  nPrior: number;
  nRows: number;
  carriedNet: number;
  rowNet: number;
  carriedTotal: number;
  rowTotal: number;
  residual: number;
  ties: boolean;
  unreadable: boolean;
  mixedTracks: boolean;
  tracks: string[];
  signMismatch: boolean;
  pairTol: number;
  // cross only: every row is dated after the period, so a tie leaves the item open on
  // Cleared in Subsequent Month instead of settling it.
  afterPeriod: boolean;
  // cross only: every row is dated before the period, so only a tie settles the item.
  beforePeriod: boolean;
  // A tie that saves but may move Net off Gross: a before-period row the previous output
  // already counted, or one it cannot be checked against. "" otherwise.
  warning: string;
  kindLabel: string;
}

export interface PriorBasketInput {
  criPicks: string[];
  insPicks: string[];
  block: UnreconciledBlock | null | undefined;
  // This period's unmatched rows per ledger: every row a basket can name.
  criLedger: FrameRecords;
  insLedger: FrameRecords;
  // The run's Matched tolerance, for a result built before manual_rules existed.
  amountTol: number;
  // result.unlisted_pre_period_rows / late_posted_pre_period_rows (frame indices).
  unlisted?: RowsByBook;
  latePosted?: RowsByBook;
}

export const BEFORE_UNCHECKED_WARNING =
  "Cannot be checked: last month's file is not this app's export, so the app cannot tell a "
  + "late posting from a row last month already counted. Pair it only if the insurer posted "
  + "it after last month's statement was taken.";
export const BEFORE_COUNTED_WARNING =
  "Check before saving: last month's output already counted this row (it is on last "
  + "month's statement, or dated before it). If so, pairing it moves Net Adjustment away "
  + "from Gross Variance by its amount. Pair it only if the insurer posted it after last "
  + "month's statement was taken.";

// WHAT A BASKET HOLDING A PRIOR-PERIOD ITEM WILL DO, or null when it holds none (the main
// manual match then applies, unchanged). The kind is inferred from where the ticks sit:
//   1+ items on one list + this period's rows on ONE ledger -> cross (the items settle
//                                                     together, judged on their net)
//   1+ items on each carried list, nothing else    -> xlist  (the halves of one break)
//   2+ items on one list, nothing else             -> offset
//   items + rows on BOTH ledgers, or items on both lists + rows -> basket
// Mirrors backend/carried_match.py::_apply_carried_manual_books' gates EXACTLY, so the
// preview can never promise an outcome the engine won't produce:
//   cross  -> on the item's own ledger the signed net vanishes; across the ledgers the
//             legs MIRROR under the declared sign frame (inverted: carried + row), or
//             compare at magnitude on an abs-match track or with no frame declared.
//             Within the strictest of the settling rows' TRACK tolerances. Rows all dated
//             after the period clear the item there: it stays open, on Cleared in
//             Subsequent Month (the engine also refuses a row that already clears one of
//             this period's, which the preview cannot see). Rows all dated before it
//             settle only on a tie, and only rows no prior-period list carries.
//   xlist  -> carried + other == 0.00               (the halves must CANCEL)
//   offset -> signed net == 0.00                    (the legs cancel exactly)
//   basket -> every leg's raw figure sums to 0.00, open rows of this period on one track
//             under the inverted frame (_resolve_basket)
export function judgePriorBasket(input: PriorBasketInput): PriorVerdict | null {
  const { criPicks, insPicks, block, criLedger, insLedger, amountTol } = input;
  const priorCri = criPicks.filter(isPriorSig);
  const priorIns = insPicks.filter(isPriorSig);
  if (!priorCri.length && !priorIns.length) return null;
  const rowCri = criPicks.filter((s) => !isPriorSig(s));
  const rowIns = insPicks.filter((s) => !isPriorSig(s));
  const rules: CarriedManualRules | undefined = block?.manual_rules;

  // ONE BASKET, not a mode. A selection the engine cannot represent is refused here
  // rather than silently truncated.
  let kind: PriorKind | null = null;
  let refusal = "";
  let book: Book = priorCri.length ? "cri" : "insurer";
  let side: Book = book;
  let items = book === "cri" ? priorCri : priorIns;
  let rows: string[] = [];
  if (priorCri.length && priorIns.length && !rowCri.length && !rowIns.length) {
    book = "insurer";
    items = priorIns;
    rows = priorCri;
    kind = "xlist";
  } else if ((priorCri.length && priorIns.length) || (rowCri.length && rowIns.length)) {
    // Items with rows on both ledgers, or items on both lists with rows (user 2026-10-22).
    book = priorIns.length ? "insurer" : "cri";
    items = [...priorIns, ...priorCri];
    rows = [...rowIns, ...rowCri];
    kind = "basket";
  } else if (rowCri.length || rowIns.length) {
    // One item, or several on one list that the same rows settled (user 2026-10-14).
    side = rowCri.length ? "cri" : "insurer";
    rows = side === "cri" ? rowCri : rowIns;
    kind = "cross";
  } else if (items.length >= 2) {
    kind = "offset";
  }

  const ledger = side === "cri" ? criLedger : insLedger;
  const late = kind === "cross" ? afterPeriodCount(ledger, rows) : 0;
  const afterPeriod = late > 0 && late === rows.length;
  // Read only without an after-period row, as the engine does (_rows_refusal).
  const early = kind === "cross" && !late ? beforePeriodCount(ledger, rows) : 0;
  const beforePeriod = early > 0 && early === rows.length;
  const labels = beforePeriod ? labelsOf(ledger, rows) : [];
  const unlistedHere = input.unlisted?.[side]?.map(String);
  // basket: the legs of both ledgers, each read on its own frame.
  const bTracks = kind === "basket"
    ? [...new Set([...tracksOf(insLedger, "_sig", rowIns), ...tracksOf(criLedger, "_sig", rowCri)])]
    : [];
  if (kind === "basket") {
    const bad = [...new Set([
      ...ineligibleCategories(insLedger, rowIns, rules?.open_categories.insurer),
      ...ineligibleCategories(criLedger, rowCri, rules?.open_categories.cri)])];
    refusal = afterPeriodCount(insLedger, rowIns) + afterPeriodCount(criLedger, rowCri)
      ? "A match across both ledgers settles against this period's rows only: untick the "
        + "rows dated after it."
      : bad.length
      ? `This period's rows sit in ${bad.map((c) => `"${c || "no category"}"`).join(", ")}, `
        + "which a prior-period item cannot clear: only an open row the engine can move to "
        + "Matched beside the item can settle it."
      : rules?.sign_frame !== "inverted"
      ? "This insurer's layout does not declare the inverted sign frame, so a match across "
        + "both ledgers cannot be checked."
      : bTracks.length > 1
      ? `These rows sit on more than one track (${bTracks.join(", ")}); a match across both `
        + "ledgers must sit on one."
      : bTracks.some((t) => rules.abs_tracks.includes(t.trim().toLowerCase()))
      ? `The ${bTracks[0]} track compares amounts at magnitude, so a match across both `
        + "ledgers cannot be checked on it."
      : "";
    if (refusal) kind = null;
  } else if (late && !afterPeriod) {
    refusal = "These rows mix this period's with rows dated after it: a prior-period item "
      + "settles against one or the other.";
    kind = null;
  } else if (early && !beforePeriod) {
    refusal = "These rows mix rows dated before the period with this period's: a "
      + "prior-period item settles against one or the other.";
    kind = null;
  } else if (beforePeriod && unlistedHere && labels.some((l) => !unlistedHere.includes(l))) {
    refusal = `${rows.length > 1 ? "One of these transactions is" : "That transaction is"} `
      + "dated before the period and a prior-period list you uploaded already carries it, so "
      + "it is open there as its own item: match the two prior-period items instead.";
    kind = null;
  } else if (kind === "cross" && !afterPeriod && !beforePeriod) {
    const bad = ineligibleCategories(ledger, rows, rules?.open_categories[side]);
    if (bad.length) {
      refusal = `This period's ${rows.length > 1 ? "rows sit" : "row sits"} in `
        + `${bad.map((c) => `"${c || "no category"}"`).join(", ")}, which a prior-period item `
        + "cannot clear: only an open row the engine can move to Matched beside the item can "
        + "settle it.";
      kind = null;
    }
  }

  // An amount that cannot be READ is not zero: an unreadable leg suppresses the verdict
  // rather than letting the preview announce a settlement it never saw.
  let unreadable = false;
  const sumOf = (frame: FrameRecords, sigCol: string, sigs: string[], names: string[]) =>
    sigs.reduce((t, s) => {
      const v = amountFor(frame, sigCol, s, names);
      if (v === null) unreadable = true;
      return t + (v ?? 0);
    }, 0);
  const isBasket = kind === "basket";
  const carriedNet = isBasket
    ? sumOf(carriedRows(block, "insurer"), CARRIED_SIG, priorIns, CARRIED_AMOUNTS)
      + sumOf(carriedRows(block, "cri"), CARRIED_SIG, priorCri, CARRIED_AMOUNTS)
    : sumOf(carriedRows(block, book), CARRIED_SIG, items, CARRIED_AMOUNTS);
  const rowNet = kind === "xlist"
    ? sumOf(carriedRows(block, "cri"), CARRIED_SIG, rows, CARRIED_AMOUNTS)
    : isBasket
    ? sumOf(insLedger, "_sig", rowIns, INS_AMOUNTS) + sumOf(criLedger, "_sig", rowCri, [CRI_AMOUNT])
    : sumOf(ledger, "_sig", rows, side === "cri" ? [CRI_AMOUNT] : INS_AMOUNTS);

  // On the item's OWN ledger a settlement is a reversal, so the signed net must vanish
  // (backend: `side == book`).
  const sameLedger = kind === "cross" && side === book;
  const tracks = kind === "cross" ? tracksOf(ledger, "_sig", rows) : [];
  const mixedTracks = tracks.length > 1;
  const track = tracks[0] ?? "";
  const frameSign = rules ? rules.sign_frame : null;
  // Rows on several tracks settle too (user 2026-10-22): an abs-match track among them
  // makes it a magnitude compare, and the strictest track tolerance applies.
  const absTrack = (tracks.length ? tracks : [track])
    .some((t) => !!rules?.abs_tracks.includes(t.trim().toLowerCase()));
  const magnitudes = kind === "cross" && !sameLedger && (!frameSign || absTrack);
  const carriedTotal = magnitudes ? Math.abs(carriedNet) : carriedNet;
  const rowTotal = magnitudes ? Math.abs(rowNet) : rowNet;
  const cents = (v: number) => Math.round(v * 100) / 100;
  // In whole cents, as backend/carried_match.py judges it.
  const raw = sameLedger ? Math.abs(carriedNet + rowNet)
    : magnitudes ? Math.abs(carriedTotal - rowTotal)
    : kind === "cross" ? Math.abs(frameSign === "inverted" ? carriedNet + rowNet
                                                           : carriedNet - rowNet)
    : kind === "xlist" || isBasket ? Math.abs(carriedNet + rowNet) : Math.abs(carriedNet);
  const residual = cents(raw);
  // Offsets and cross-list pairs must cancel EXACTLY: both legs leave their group lines,
  // so a residual would sit on no Overview line. A row pair takes its track's tolerance.
  const pairTol = kind !== "cross" ? 0
    : rules ? (rules.tol_by_track
      ? Math.min(...(tracks.length ? tracks : [track])
        .map((t) => rules.tol_by_track?.[t] ?? rules.tol))
      : rules.tol)
    : amountTol;
  const ties = !!kind && !unreadable && residual <= pairTol;
  // Agrees at magnitude but not in sign: across the ledgers that is a receipt clearing an
  // invoice, not one document booked on both books.
  const signMismatch = kind === "cross" && !sameLedger && !magnitudes && !ties
    && cents(Math.abs(Math.abs(carriedNet) - Math.abs(rowNet))) <= pairTol;
  // A row dated before the period has no Group 3 to hold a gap, so it settles on a tie or
  // not at all (backend _apply_carried_manual_books).
  if (beforePeriod && kind && !ties && !unreadable) {
    refusal = `${rows.length > 1 ? "These transactions are" : "That transaction is"} dated `
      + `before the period, so ${rows.length > 1 ? "they settle" : "it settles"} a `
      + "prior-period item only when the two tie, and "
      + (signMismatch ? "the signs do not mirror the way this insurer's matched rows do."
        : `they differ by ${residual.toFixed(2)}.`);
    kind = null;
  }
  // Only a late posting (a row last month's statement never saw) is new money in the
  // opening; pairing any other moves Net off Gross by its amount (user 2026-11-02: allow,
  // but warn).
  const lateHere = input.latePosted?.[side]?.map(String);
  const warning = !(beforePeriod && kind && ties) ? ""
    : !lateHere ? BEFORE_UNCHECKED_WARNING
    : labels.some((l) => !lateHere.includes(l)) ? BEFORE_COUNTED_WARNING : "";

  const pair: CarriedMatchPair | null =
    kind === "offset" ? { carried_sigs: [...items] }
      : kind === "xlist" ? (items.length === 1 && rows.length === 1
        ? { insurer_carried_sig: items[0], cri_carried_sig: rows[0] }
        : { insurer_carried_sigs: [...items], cri_carried_sigs: [...rows] })
      : kind === "cross" ? (items.length > 1
        ? { carried_sigs: [...items], row_sigs: [...rows], side }
        : rows.length > 1
        ? { carried_sig: items[0], row_sigs: [...rows], side }
        : { carried_sig: items[0], row_sig: rows[0], side })
      : isBasket ? { basket: {
          items: { ...(priorIns.length ? { insurer: [...priorIns] } : {}),
                   ...(priorCri.length ? { cri: [...priorCri] } : {}) },
          rows: { ...(rowIns.length ? { insurer: [...rowIns] } : {}),
                  ...(rowCri.length ? { cri: [...rowCri] } : {}) } } }
      : null;

  const nRows = rows.length;
  return {
    kind, refusal, book, side, pair, nPrior: priorCri.length + priorIns.length, nRows,
    carriedNet, rowNet, carriedTotal, rowTotal, residual, ties, unreadable, mixedTracks,
    tracks: isBasket ? bTracks : tracks, signMismatch, pairTol, afterPeriod, beforePeriod,
    warning,
    kindLabel: isBasket
      ? `${items.length} prior-period item${items.length > 1 ? "s" : ""}`
        + `${priorCri.length && priorIns.length ? " on both lists" : ""} ⟷ ${nRows} row`
        + `${nRows > 1 ? "s" : ""} this period${rowCri.length && rowIns.length ? " on both ledgers" : ""}`
      : kind === "cross"
      ? (items.length > 1 ? `${items.length} prior-period items ⟷ ${nRows > 1 ? `${nRows} rows` : "1 row"} this period`
        : nRows > 1 ? `Prior-period item ⟷ ${nRows} rows this period` : "Prior-period item ⟷ this period")
      : kind === "xlist" ? (items.length === 1 && nRows === 1 ? "Insurer-list item ⟷ CRI-list item"
        : `${items.length} insurer-list item${items.length > 1 ? "s" : ""} ⟷ `
          + `${nRows} CRI-list item${nRows > 1 ? "s" : ""}`)
        : kind === "offset" ? `Prior-period offset · ${items.length} items` : "",
  };
}

// Ticked carried items with an identical copy on their list. Twins differing in date or
// row are keyed apart (backend carried_signatures); copies alike in both differ only by
// their "#k" file position, so a saved match may attach to another of them next month.
const COPY_SUFFIX = /#\d+$/;
export function carriedCopies(frame: FrameRecords, picks: string[]): { sig: string; total: number }[] {
  if (!picks.length) return [];
  const si = frame.columns.indexOf(CARRIED_SIG);
  const xi = frame.columns.indexOf("unrec_excluded");
  if (si < 0) return [];
  const n = new Map<string, number>();
  for (const r of frame.rows) {
    if (xi >= 0 && r[xi]) continue;
    const k = String(r[si] ?? "").replace(COPY_SUFFIX, "");
    n.set(k, (n.get(k) ?? 0) + 1);
  }
  const total = (s: string) => n.get(s.replace(COPY_SUFFIX, "")) ?? 0;
  return picks.filter((s) => total(s) > 1).map((s) => ({ sig: s, total: total(s) }));
}

// The rows of `frame` whose `sigCol` is in `sigs`, in `sigs` order: a match's legs.
export function sliceBySigs(frame: FrameRecords | undefined, sigCol: string, sigs: string[]): FrameRecords {
  if (!frame) return EMPTY;
  const si = frame.columns.indexOf(sigCol);
  if (si < 0) return EMPTY;
  const at = new Map<string, number>();
  frame.rows.forEach((r, i) => { const k = String(r[si] ?? ""); if (!at.has(k)) at.set(k, i); });
  const pos = sigs.map((s) => at.get(s)).filter((p): p is number => p !== undefined);
  return { columns: frame.columns, rows: pos.map((p) => frame.rows[p]),
           indices: pos.map((p) => frame.indices[p] ?? p), total_rows: pos.length };
}
